import { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useSession } from "../../auth/session"
import Api from "../../api/api"
import { UserSearchResult, UserRole } from "../../api/models"
import Spinner from "../widgets/Spinner"
import Avatar from "../widgets/Avatar"

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2

const roleLabels: Record<UserRole, string> = {
  [UserRole.Admin]: "Admin",
  [UserRole.Customer]: "Customer",
}

// warn only on a definite "not verified" — an absent attribute is unknown
function UnverifiedEmailWarning({ verified }: { verified?: boolean }) {
  if (verified !== false) return null
  return (
    <span title="Email not verified" style={{ marginLeft: 6, cursor: "help" }} role="img" aria-label="Email not verified">
      ⚠️
    </span>
  )
}

function UserRow({ user }: { user: UserSearchResult }) {
  const name = [user.firstname, user.lastname].filter(Boolean).join(" ")
  return (
    <tr>
      <td>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Avatar imageUrl={user.imageUrl} size={24} />
          <Link to={`/users/${user.id}`} className="card-link">{user.id}</Link>
          {user.enabled === false && (
            <span title="Access disabled" style={{ cursor: "help" }} role="img" aria-label="Access disabled">
              🚫
            </span>
          )}
        </span>
      </td>
      <td>{name || "—"}</td>
      <td>
        {user.email ?? "—"}
        {user.email && <UnverifiedEmailWarning verified={user.emailVerified} />}
      </td>
      <td>{user.role ? roleLabels[user.role] : "—"}</td>
    </tr>
  )
}

// Without a search term: pages through the whole Cognito pool (link cursor).
// With one (≥2 chars, debounced): capped, unpaginated prefix search replaces
// the listing until cleared. Local state like SkuEntitlements — see the note
// there about the singleton redux pagination helpers.
export default function Users() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""

  const [searchText, setSearchText] = useState("")
  const [users, setUsers] = useState<UserSearchResult[] | undefined>(undefined)
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // stale responses (search text changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  const searchActive = searchText.trim().length >= SEARCH_MIN_CHARS

  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.listUsers({ link: cursor, count: PAGE_SIZE, accessToken })
      if (seq !== requestSeq.current) return
      setUsers((prev) => (cursor === null ? page.users : [...(prev ?? []), ...page.users]))
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [accessToken])

  useEffect(() => {
    if (searchActive) {
      const seq = ++requestSeq.current
      setUsers(undefined)
      setLink(null)
      setLoading(true)
      setError(false)
      const timer = setTimeout(async () => {
        try {
          const page = await Api.searchUsers({ search: searchText.trim(), accessToken })
          if (seq !== requestSeq.current) return
          setUsers(page.users)
        } catch {
          if (seq === requestSeq.current) setError(true)
        } finally {
          if (seq === requestSeq.current) setLoading(false)
        }
      }, SEARCH_DEBOUNCE_MS)
      return () => clearTimeout(timer)
    } else {
      setUsers(undefined)
      setLink(null)
      loadPage(null)
    }
  }, [searchActive, searchText, accessToken, loadPage])

  // search results are capped and unpaginated — no cursor to follow
  const haveMore = !searchActive && link !== null

  return (
    <div className="page">
      <h1 className="page-title">Users</h1>

      <div className="filter-bar">
        <input
          className="filter-input"
          type="text"
          placeholder="ID, name or email…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {error && users === undefined ? (
        <p className="empty-state">Error loading users</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full name</th>
                  <th>Email</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {users === undefined ? (
                  <tr><td colSpan={4}><Spinner size={16} /></td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={4}>{searchActive ? "No matching users" : "No users"}</td></tr>
                ) : (
                  users.map((user) => <UserRow key={user.id} user={user} />)
                )}
              </tbody>
            </table>
          </div>
          <div style={{ marginLeft: "8px" }}>
            {haveMore && users !== undefined && (
              loading ? <Spinner size={16} /> : (
                <button className="card-link" onClick={() => loadPage(link)} style={{ background: "none", border: "none", cursor: "pointer" }}>
                  Load more
                </button>
              )
            )}
            {error && users !== undefined && (
              <p className="empty-state">Error loading more users</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
